<template>
    <div>
        <kt-datatable
            :model="formModel"
            :data="rows"
            :filter="datatableFilter"
            :meta="{ tableClass: 'table', rowClass: getRowClass }"
            :update-function="handleDatatableUpdate"
            :hide-search="false"
            @cell-click="handleCellClick"
            storage-key="form-template-list"
        >
            <template #header_select="scope">
                <div class="form-check">
                    <input 
                        class="form-check-input" 
                        type="checkbox" 
                        :checked="selectAll"
                        @change="toggleSelectAll"
                        @click.stop
                        title="Select all on this page"
                    />
                </div>
            </template>
            
            <template #view_select="scope">
                <div class="form-check">
                    <input 
                        class="form-check-input" 
                        type="checkbox" 
                        :checked="selectedRows.includes(scope.row.Id)"
                        @change="toggleRowSelection(scope.row.Id)"
                        @click.stop
                    />
                </div>
            </template>

            <template #filters="scope">
                <div class="col">
                    <div class="row">
                        <div class="col-auto">
                            <form-category-multi-check 
                                label="Category"
                                :model-value="currentFilter.Category ? [currentFilter.Category] : []"
                                @update:model-value="(value) => setFilter({ Category: value?.[0] || undefined })"
                            />
                        </div>
                        <div class="col-auto">
                            <form-status-multi-check 
                                label="Status"
                                :model-value="currentFilter.IsActive !== undefined ? [currentFilter.IsActive] : []"
                                @update:model-value="(value) => setFilter({ IsActive: value?.[0] })"
                            />
                        </div>
                    </div>
                </div>
            </template>

            <template #after-search="scope">
                <div class="btn-group">
                    <button @click="refresh()" 
                        class="btn btn-sm btn-outline-secondary me-2"
                    >Refresh <i class="fa fa-sync"></i></button>

                    <button @click="newRecord()" 
                        class="btn btn-sm btn-outline-primary"
                    >New <i class="fa fa-plus"></i></button>
                </div>
            </template>

            <template #under-header>
                <div v-if="selectedRows.length > 0" class="mb-0 p-2 actions-row">
                    <div class="d-flex align-items-center gap-2">
                        <span class="fw-bold text-primary">{{ selectedRows.length }} selected</span>
                        <button 
                            class="btn btn-sm btn-outline-danger"
                            @click="showDeleteModal = true"
                            title="Delete Selected"
                        >
                            <i class="fas fa-trash"></i> Delete
                        </button>
                    </div>
                </div>
            </template>

            <template #view_actions="scope">
                <div class="btn-group btn-group-sm">
                    <button 
                        class="btn btn-outline-primary btn-sm"
                        @click.stop="openGenerateLinkModal(scope.row as FormTemplateModel)"
                        title="Generate Public Link"
                    >
                        <i class="fas fa-link"></i>
                    </button>
                </div>
            </template>

            <template #view_string="scope">
                <span v-if="scope.column.name === 'IsActive'">
                    <span :class="scope.row.IsActive ? 'badge bg-success' : 'badge bg-secondary'">
                        {{ scope.row.IsActive ? 'Active' : 'Inactive' }}
                    </span>
                </span>
                <span v-else-if="scope.column.name === 'DateCreated'">
                    {{ formatDate(scope.row.DateCreated) }}
                </span>
                <span v-else-if="scope.column.name === 'DateModified'">
                    {{ formatDate(scope.row.DateModified) }}
                </span>
                <span v-else>
                    {{ scope.value }}
                </span>
            </template>
        </kt-datatable>

        <!-- Generate Link Modal -->
        <div v-if="showLinkModal" class="modal fade show" style="display: block;" tabindex="-1">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Generate Public Form Link</h5>
                        <button type="button" class="btn-close" @click="closeLinkModal"></button>
                    </div>
                    <div class="modal-body">
                        <div v-if="!generatedUrl">
                            <p class="mb-3">Generate a public link for: <strong>{{ selectedTemplate?.Title }}</strong></p>
                            
                            <div class="mb-3">
                                <div class="form-check">
                                    <input 
                                        class="form-check-input" 
                                        type="checkbox" 
                                        id="allowEditing"
                                        v-model="linkOptions.allowEditing"
                                    />
                                    <label class="form-check-label" for="allowEditing">
                                        Allow Multiple Edits
                                    </label>
                                    <div class="form-text">If unchecked, the form can only be submitted once (one-time use).</div>
                                </div>
                            </div>

                            <div class="mb-3">
                                <label class="form-label">Expiration (optional)</label>
                                <input 
                                    type="datetime-local" 
                                    class="form-control"
                                    v-model="linkOptions.expiresAt"
                                />
                            </div>

                            <div class="mb-3">
                                <label class="form-label">Max Uses (optional)</label>
                                <input 
                                    type="number" 
                                    class="form-control"
                                    v-model="linkOptions.maxUses"
                                    min="1"
                                    placeholder="Unlimited"
                                />
                            </div>

                            <div class="mb-3">
                                <label class="form-label">Send To Email (optional)</label>
                                <input 
                                    type="email" 
                                    class="form-control"
                                    v-model="linkOptions.sentToEmail"
                                    placeholder="recipient@example.com"
                                />
                            </div>
                        </div>

                        <div v-else>
                            <p class="text-success mb-3"><i class="fas fa-check-circle"></i> Link generated successfully!</p>
                            
                            <div class="mb-3">
                                <label class="form-label">Public URL</label>
                                <div class="input-group">
                                    <input 
                                        type="text" 
                                        class="form-control" 
                                        :value="generatedUrl" 
                                        readonly
                                        ref="urlInput"
                                    />
                                    <button 
                                        class="btn btn-outline-secondary" 
                                        type="button"
                                        @click="copyUrl"
                                    >
                                        <i class="fas fa-copy"></i> Copy
                                    </button>
                                </div>
                                <div v-if="urlCopied" class="form-text text-success">Copied to clipboard!</div>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" @click="closeLinkModal">Close</button>
                        <button 
                            v-if="!generatedUrl"
                            type="button" 
                            class="btn btn-primary" 
                            @click="generateLink"
                            :disabled="generatingLink"
                        >
                            <span v-if="generatingLink"><i class="fas fa-spinner fa-spin"></i> Generating...</span>
                            <span v-else>Generate Link</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="showLinkModal" class="modal-backdrop fade show"></div>

        <!-- Delete Confirmation Modal -->
        <div v-if="showDeleteModal" class="modal fade show" style="display: block;" tabindex="-1">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Confirm Delete</h5>
                        <button type="button" class="btn-close" @click="showDeleteModal = false"></button>
                    </div>
                    <div class="modal-body">
                        <p>Are you sure you want to delete {{ selectedRows.length }} form template(s)?</p>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" @click="showDeleteModal = false">Cancel</button>
                        <button type="button" class="btn btn-danger" @click="deleteSelected">Delete</button>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="showDeleteModal" class="modal-backdrop fade show"></div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useRouter } from "vue-router";
import Api, { ApiResponse, FormTemplateModel, FormTemplateFilter } from "@/services/ApiService";
import Emitter from "@/services/Emitter";
import KtDatatable from "@/components/UI/KtDatatable.vue";
import Helper, { DateFormat } from "@/services/Helper";
import FormCategoryMultiCheck from "@/components/Forms/FormCategoryMultiCheck.vue";
import FormStatusMultiCheck from "@/components/Forms/FormStatusMultiCheck.vue";

const props = defineProps({
    embedded: { type: Boolean, default: false },
    hideFilters: { type: Boolean, default: false }
});

const emit = defineEmits(['edit', 'new']);

const router = useRouter();

// Data
const rows = ref<FormTemplateModel[]>([]);
const total = ref(0);
const selectedRows = ref<string[]>([]);
const showDeleteModal = ref(false);
const categories = ref<string[]>([]);

// Link generation modal state
const showLinkModal = ref(false);
const selectedTemplate = ref<FormTemplateModel | null>(null);
const generatedUrl = ref<string>('');
const generatingLink = ref(false);
const urlCopied = ref(false);
const urlInput = ref<HTMLInputElement | null>(null);
const linkOptions = ref({
    allowEditing: true,
    expiresAt: '',
    maxUses: null as number | null,
    sentToEmail: ''
});

// Filter state
const currentFilter = ref<FormTemplateFilter>({
    Page: 0,
    Size: 25,
    IsActive: undefined,
    Category: undefined,
    SearchTerm: undefined
});

const datatableFilter = computed(() => ({
    ...currentFilter.value,
    total: total.value
}));

const selectAll = computed(() => {
    return rows.value.length > 0 && rows.value.every(row => selectedRows.value.includes(row.Id!));
});

// Datatable model
const formModel = [
    {
        name: "Select",
        label: "",
        type: "select",
        display: true,
        width: 40
    },
    {
        name: "Title",
        label: "Title",
        type: "string",
        display: true,
        sortable: true
    },
    {
        name: "Description",
        label: "Description",
        type: "string",
        display: true
    },
    {
        name: "RefType",
        label: "Type",
        type: "string",
        display: true,
        sortable: true
    },
    {
        name: "Category",
        label: "Category",
        type: "string",
        display: true,
        sortable: true
    },
    {
        name: "IsActive",
        label: "Status",
        type: "string",
        display: true
    },
    {
        name: "Version",
        label: "Version",
        type: "number",
        display: true
    },
    {
        name: "DateCreated",
        label: "Created",
        type: "string",
        display: true,
        sortable: true
    },
    {
        name: "Actions",
        label: "Actions",
        type: "actions",
        display: true,
        width: 100
    }
];

// Methods
const loadData = async () => {
    try {
        const response: ApiResponse<FormTemplateModel[]> = await Api.loadFormTemplates(currentFilter.value);
        rows.value = response.Data || [];
        total.value = response.Meta?.Total || 0;
        
        // Extract unique categories
        const cats = new Set<string>();
        rows.value.forEach(row => {
            if (row.Category) cats.add(row.Category);
        });
        categories.value = Array.from(cats).sort();
    } catch (error) {
        console.error('Failed to load form templates:', error);
        rows.value = [];
        total.value = 0;
    }
};

const handleDatatableUpdate = async (filter: any) => {
    currentFilter.value = { ...currentFilter.value, ...filter };
    await loadData();
};

const applyFilter = () => {
    currentFilter.value.Page = 0;
    loadData();
};

const setFilter = (filter: Partial<FormTemplateFilter>) => {
    currentFilter.value = { ...currentFilter.value, ...filter, Page: 0 };
    loadData();
};

const refresh = () => {
    loadData();
};

const newRecord = () => {
    if (props.embedded) {
        emit('new');
    } else {
        router.push('/form/edit/new');
    }
};

const editRecord = (id: string) => {
    if (props.embedded) {
        emit('edit', id);
    } else {
        router.push(`/form/edit/${id}`);
    }
};

const handleCellClick = (row: any, column: any, value: any, rowIndex: number) => {
    if (column.name !== 'Select') {
        editRecord(row.Id);
    }
};

const getRowClass = (row: FormTemplateModel) => {
    return row.IsActive ? '' : 'table-secondary';
};

const toggleSelectAll = () => {
    if (selectAll.value) {
        selectedRows.value = [];
    } else {
        selectedRows.value = rows.value.map(row => row.Id!);
    }
};

const toggleRowSelection = (id: string) => {
    const index = selectedRows.value.indexOf(id);
    if (index === -1) {
        selectedRows.value.push(id);
    } else {
        selectedRows.value.splice(index, 1);
    }
};

const deleteSelected = async () => {
    try {
        for (const id of selectedRows.value) {
            await Api.deleteFormTemplate(id);
        }
        selectedRows.value = [];
        showDeleteModal.value = false;
        await loadData();
    } catch (error) {
        console.error('Failed to delete form templates:', error);
    }
};

const formatDate = (date: string | undefined) => {
    if (!date) return '';
    try {
        const d = new Date(date);
        return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
        return date;
    }
};

// Link generation methods
const openGenerateLinkModal = (template: FormTemplateModel) => {
    selectedTemplate.value = template;
    generatedUrl.value = '';
    urlCopied.value = false;
    linkOptions.value = {
        allowEditing: true,
        expiresAt: '',
        maxUses: null,
        sentToEmail: ''
    };
    showLinkModal.value = true;
};

const closeLinkModal = () => {
    showLinkModal.value = false;
    selectedTemplate.value = null;
    generatedUrl.value = '';
};

const generateLink = async () => {
    if (!selectedTemplate.value?.Id) return;
    
    generatingLink.value = true;
    try {
        const response = await Api.createFormAccessLinkForTemplate({
            TemplateId: selectedTemplate.value.Id,
            AllowEditing: linkOptions.value.allowEditing,
            ExpiresAt: linkOptions.value.expiresAt ? new Date(linkOptions.value.expiresAt).toISOString() : undefined,
            MaxUses: linkOptions.value.maxUses || undefined,
            SentToEmail: linkOptions.value.sentToEmail || undefined
        });
        
        if (response.Status === 1 && response.Data?.PublicUrl) {
            generatedUrl.value = response.Data.PublicUrl;
        } else {
            console.error('Failed to generate link:', response.Message);
        }
    } catch (error) {
        console.error('Failed to generate link:', error);
    } finally {
        generatingLink.value = false;
    }
};

const copyUrl = async () => {
    if (generatedUrl.value) {
        try {
            await navigator.clipboard.writeText(generatedUrl.value);
            urlCopied.value = true;
            setTimeout(() => {
                urlCopied.value = false;
            }, 2000);
        } catch (error) {
            console.error('Failed to copy URL:', error);
        }
    }
};

// Lifecycle
onMounted(() => {
    loadData();
    Emitter.on('datatable-refresh', (key: string) => {
        if (key === 'form-template-list') {
            loadData();
        }
    });
});

onUnmounted(() => {
    // Cleanup handled by Vue
});
</script>

<style scoped>
.actions-row {
    background-color: #f8f9fa;
    border-bottom: 1px solid #dee2e6;
}
</style>
